import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import AddActionButton from 'components/AddActionButton';
import AddButton from 'components/AddButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import CheckBox from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithTextGetter
} from 'components/final-form/dropdownUtils';
import Select from 'components/final-form/Select';
import InterventionSlotView from 'components/intervention-availability/InterventionSlotView';
import UpsertInterventionAvailabilitySlotForm
  from 'components/intervention-availability/UpsertInterventionAvailabilitySlotForm';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import SearchResultComponent from 'components/SearchResultComponent';
import { FormState } from 'final-form';
import moment from 'moment';
import React, { Component } from "react";
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { Button, Grid, Icon, Modal, Popup } from 'semantic-ui-react';
import { deleteInterventionAvailability } from 'services/interventionAvailabilityServices';
import { getActiveInterventions } from 'services/interventionServices';
import { getAllShippableMedicaments } from 'services/medicamentServices';
import { getResources } from 'services/patientCentralServices';
import { searchInterventionAvailabilities } from 'services/slotManagementServices';
import styled from 'styled-components';
import {
  InterventionAvailabilityDto,
  InterventionAvailabilitySearchRequest,
  InterventionDto,
  MedicamentDto,
  ResourceDto,
  UpsertSlotDto
} from "ts-types/api.types";
import { isoToDisplayTime, toIsoDateString } from 'utils/dateUtils';
import { noop } from 'utils/functionUtils';
import { applyStyles } from 'utils/textUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const StatsCellDiv = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: baseline;

  .percentage {
    margin-right: 0.35rem;
  }
`;

const StatsPopupDiv = styled.div`
  .title {
    width: 100%;
    border-bottom: 1px solid #c5c5c5;
    font-weight: bold;
  }

  .content {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;

    .percentage-booked {
      margin: 1.5rem 1rem 1.5rem 0.75rem;
      font-weight: bold;
      font-size: 2.3rem;
    }

    .stats-text {
      .highlight-value {
        color: rgb(48 145 218);
        font-weight: bold;
      }
    }
  }
`;

export enum InterventionAvailabilityType {
  VACCINATION,
  OTHER
}

interface InterventionAvailabilitySearchRequestFieldProps extends FormState {
  values: Partial<InterventionAvailabilitySearchRequest>
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps, RouteComponentProps<any> {

}

interface State {
  pageDataLoaded: boolean;
  interventionAvailabilityType: InterventionAvailabilityType,

  resources: ResourceDto[];
  resourcesOptions: DropdownOption[];
  interventions: InterventionDto[];
  interventionsMap: Map<number, InterventionDto>;
  interventionsOptions: DropdownOption[];
  medicaments: MedicamentDto[];
  medicamentsOptions: DropdownOption[];

  searchValues: Partial<InterventionAvailabilitySearchRequest>;

  foundSlots: InterventionAvailabilityDto[],
  availableSlotsModalOpen: boolean,
  addEditModalOpen: boolean,
  slotId?: number,
  slot?: UpsertSlotDto,
  interventionAvailabilityId?: number,
  slotsLoaded: boolean,
  cancelTokenSource: CancelTokenSource
}

class InterventionAvailabilityView extends Component<Props, State> {

  initialSearchValues: Partial<InterventionAvailabilitySearchRequest> = {};
  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.initialSearchValues = {
      mandateId: props.getMandate(),
      fromDate: toIsoDateString(moment().subtract(1, "month")),
      toDate: toIsoDateString(moment().add(2, "month")),
      includingInactive: true,
      withSelfService: true
    }

    this.state = {
      pageDataLoaded: false,
      interventionAvailabilityType: InterventionAvailabilityType.VACCINATION,

      resources: [],
      resourcesOptions: [],
      interventions: [],
      interventionsMap: new Map<number, InterventionDto>(),
      interventionsOptions: [],
      medicaments: [],
      medicamentsOptions: [],

      searchValues: this.initialSearchValues,

      foundSlots: [],
      availableSlotsModalOpen: false,
      addEditModalOpen: false,
      slotsLoaded: false,
      cancelTokenSource: cancelTokenSource
    }

    this.fetchPageData(props);

    this.onSearchSlotsSubmitClicked();
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;
    const {cancelTokenSource} = this.state;

    cancelTokenSource.cancel('Operation canceled by user');
  }

  fetchPageData = async (props: Props) => {
    const {cancelTokenSource} = this.state;

    Promise.all([
          getResources(props.getMandate(), cancelTokenSource),
          getActiveInterventions(cancelTokenSource),
          getAllShippableMedicaments(cancelTokenSource)
        ])
        .then(result => {

          const resources: ResourceDto[] = result[0];
          const interventions: InterventionDto[] = result[1];
          const medicaments = result[2];

          let interventionsMap: Map<number, InterventionDto> = new Map<number, InterventionDto>();
          result[1].forEach(intervention => {
            interventionsMap.set(intervention.id, intervention);
          });

          const resourcesOptions = mapToDropdownOptionArray(resources, "description", "id");

          const interventionOptions = mapToDropdownOptionArray(interventions, "description", "id");

          const medicamentOptions = mapToDropdownOptionArrayWithTextGetter(
              medicaments, medicamant => `${medicamant.code} - ${medicamant.description}`, "id");

          this.setState({
            resources,
            resourcesOptions: resourcesOptions,
            interventions,
            interventionsMap,
            interventionsOptions: interventionOptions,
            medicaments,
            medicamentsOptions: medicamentOptions
          });

        })
        .finally(() => {
          this.setState({
            pageDataLoaded: true
          });
        });
  }

  activateSlot = (slotId: number) => {
    const {cancelTokenSource} = this.state;

    // activateSlot(slotId, cancelTokenSource)
    //     .then(() => {
    //       const { foundSlots } = this.state;
    //
    //       const modifiedSlots = Array.from(foundSlots);
    //
    //       const foundSlotIndex = modifiedSlots.findIndex((slot: SlotDto) => slot.id === slotId);
    //
    //       modifiedSlots[foundSlotIndex] = {
    //         ...modifiedSlots[foundSlotIndex],
    //         active: true
    //       }
    //
    //       this.setState({
    //         foundSlots: modifiedSlots
    //       })
    //     })
    //     .catch(noop);
  }

  openAvailabilitiesSegment = () => {
    const {interventions, resources} = this.state;
    this.props.history.push(
        "/settings/intervention-availability-config/availabilities",
        {
          interventions: interventions,
          resources: resources
        }
    );
  }

  openAvailableSlotsModal = (slotId?: number) => {
    if (slotId) {
      this.setState({
        availableSlotsModalOpen: true,
        interventionAvailabilityId: slotId
      });
    }
  }

  openAddEditModal = (type: InterventionAvailabilityType, slotId?: number) => {
    this.setState({
      addEditModalOpen: true,
      interventionAvailabilityType: type,
      interventionAvailabilityId: slotId
    });
  }

  hideModal = () => {
    this.setState({
      availableSlotsModalOpen: false,
      addEditModalOpen: false,
      slotId: undefined,
      slot: undefined
    });
  };

  deleteSlot = (id: number) => {
    const {cancelTokenSource} = this.state;

    deleteInterventionAvailability(id, cancelTokenSource)
        .then(() => {
          this.onSearchSlotsSubmitClicked();
        })
        .catch(noop);
  }

  handleChange = async ({values}: InterventionAvailabilitySearchRequestFieldProps) => {
    if (!this.mounted) {
      return;
    }

    this.setState({
          searchValues: values
        },
        () => {
          this.onSearchSlotsSubmitClicked();
        });


  }

  onSearchSlotsSubmitClicked = async () => {
    // if (!this.mounted) {
    //   return;
    // }

    const {searchValues, cancelTokenSource} = this.state;

    this.setState({
      slotsLoaded: false
    })

    const onFinally = () => {
      this.setState({
        slotsLoaded: true
      })
    };

    searchInterventionAvailabilities(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundSlots: response
          })
        })
        .finally(onFinally)
  }

  render() {
    const {pageDataLoaded, slotsLoaded} = this.state;
    const {getMessage} = this.props;

    if (!pageDataLoaded) {
      return <></>;
    }

    return (
        <PraxStyledSegment id="intervention-availability-box">
          <div>
            {this.renderSearchSlotsSegment()}
          </div>
          <StyledViewTableRow>
            {!slotsLoaded ?
                <LoaderComponent message={getMessage("slots.loading")}/> : this.renderSlotsSegment()}
          </StyledViewTableRow>
          {this.renderInterventionSlotModal()}
          {this.renderUpsertInterventionAvailabilityModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchSlotsSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={this.onSearchSlotsSubmitClicked}
            initialValues={this.initialSearchValues}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  }

  renderSearchForm = ({handleSubmit, submitting}: FormRenderProps): JSX.Element => {
    const {getMessage} = this.props;
    const {foundSlots} = this.state;

    const numResults = foundSlots.length;

    return (
        <form onSubmit={handleSubmit}>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>

          <PxGrid>
            <Grid.Row>
              <Grid.Column width={2} verticalAlign="middle">
                {getMessage("interventionAvailability.searchForm.dateFrom")}:
              </Grid.Column>
              <Grid.Column width={3}>
                <Field
                    name="fromDate"
                    component={DatePicker}
                    fluid
                />
              </Grid.Column>
              <Grid.Column width={2} verticalAlign="middle">
                {getMessage("to")}:
              </Grid.Column>
              <Grid.Column width={3}>
                <Field
                    name="toDate"
                    component={DatePicker}
                    fluid
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={2} verticalAlign="middle">
                {getMessage("interventionAvailability.searchForm.intervention")}:
              </Grid.Column>
              <Grid.Column width={8}>
                <Field
                    name="interventionIds"
                    fluid
                    component={Select}
                    options={this.state.interventionsOptions}
                    multiple={true}
                    clearable
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={2} verticalAlign="middle">
                {getMessage("interventionAvailability.searchForm.resource")}:
              </Grid.Column>
              <Grid.Column width={8}>
                <Field
                    name="resourceIds"
                    fluid
                    component={Select}
                    options={this.state.resourcesOptions}
                    multiple={true}
                    clearable
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={2}/>
              <Grid.Column width={3}>
                <Field
                    label={getMessage("includingInactive")}
                    name="includingInactive"
                    component={CheckBox}
                    toggle={true}
                />
              </Grid.Column>
              <Grid.Column width={5} verticalAlign={"middle"}>
                <Field
                    label={getMessage("interventionAvailability.withSelfService")}
                    name="withSelfService"
                    component={CheckBox}
                    toggle={true}
                />
              </Grid.Column>
            </Grid.Row>
            {/*<Grid.Row>
              <Grid.Column width={2} verticalAlign="middle">
                {getMessage("interventionAvailability.searchForm.intervention")}:
              </Grid.Column>
              <Grid.Column width={3}>
                <Field
                    name="vaccineProvider"
                    fluid
                    // TODO ftrajkov 2021-05-13: Add correct options
                    component={Select}
                    options={[]}
                />
              </Grid.Column>
            </Grid.Row>*/}
            <Grid.Row>
              <Grid.Column width={5}>
                <Button type="submit" primary compact disabled={submitting}>
                  {getMessage("search")}
                </Button>
              </Grid.Column>
              <Grid.Column width={11}>
                <AddButton
                    type="button"
                    buttonMessage={getMessage("interventionAvailability.action.addOtherAvailability")}
                    onClick={() => this.openAddEditModal(InterventionAvailabilityType.OTHER)}
                />
                <AddButton
                    type="button"
                    buttonMessage={getMessage("interventionAvailability.action.addAvailability")}
                    onClick={() => this.openAddEditModal(InterventionAvailabilityType.VACCINATION)}
                />
              </Grid.Column>
            </Grid.Row>
            <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          </PxGrid>
        </form>
    );
  }

  renderSlotsSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundSlots} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundSlots.length}
            rowGetter={this.slotsRowGetter}
            rowRenderer={this.foundSlotsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 50,
                label: "",
                dataKey: "id",
                flexGrow: 0,
                flexShrink: 0,
                cellRenderer: (this.slotStatsCellRenderer)
              },
              {
                width: 225,
                flexGrow: 0,
                flexShrink: 0,
                label: (getMessage("date")),
                dataKey: "date",
                cellRenderer: (this.dateCellRenderer)
              },
              {
                width: 120,
                flexGrow: 0,
                flexShrink: 0,
                label: (getMessage("time")),
                dataKey: "time",
                cellRenderer: (this.timeCellRenderer)
              },
              {
                width: 100,
                flexGrow: 0,
                flexShrink: 0,
                label: (getMessage("interventionAvailability.form.active")),
                dataKey: "active",
                cellRenderer: (this.statusCellRenderer)
              },
              {
                width: 100,
                flexGrow: 0,
                flexShrink: 0,
                label: (getMessage("interventionAvailability.form.selfService")),
                dataKey: "bookingSituation",
                cellRenderer: (this.selfServiceCellRenderer)
              },
              {
                width: 140,
                flexGrow: 1,
                flexShrink: 0,
                label: (getMessage("interventionAvailability.form.resourceId")),
                dataKey: "city",
                cellRenderer: (this.placeCellRenderer)
              },
              {
                width: 190,
                flexGrow: 1,
                flexShrink: 0,
                label: (getMessage("interventionAvailability.form.interventionId")),
                dataKey: "interventions",
                cellRenderer: (this.interventionCellRenderer)
              },
              {
                width: 220,
                flexGrow: 2,
                flexShrink: 0,
                label: (getMessage("interventionAvailability.form.medicamentId")),
                dataKey: "vaccineProvider",
                cellRenderer: (this.medicamentCellRenderer)
              },
              {
                width: 100,
                flexGrow: 0,
                flexShrink: 0,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  slotsRowGetter = ({index}: any) => {
    const {foundSlots} = this.state;

    Object.assign(foundSlots[index], {index: index + 1});

    return foundSlots[index]
  };

  foundSlotsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundSlots} = this.state;

    const availability = foundSlots[index];

    const slotId = availability.id;
    const active = availability.active;

    let type = InterventionAvailabilityType.VACCINATION;
    if (availability.medicamentId === null && availability.lotNumber === null && availability.minimumAge === null) {
      type = InterventionAvailabilityType.OTHER;
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(type, slotId)}
        >
          {columns}
        </div>
    );
  };

  slotStatsCellRenderer = (data: any) => {

    const {getMessage} = this.props;
    const {rowData} = data;

    let trigger = (<Icon
        name="chart bar outline"
        title={getMessage("interventionAvailability.tableRow.slotStats")}/>);

    const totalSlots = rowData.totalSlots;
    const percentBooked = totalSlots !== 0 ? Math.round((rowData.numberNotFreeSlots / totalSlots) * 100) : 0;
    const percentBookedString = `${percentBooked}%`;

    return (
        <StatsCellDiv>
          <div className="percentage">{percentBookedString}</div>
          <Popup
              trigger={trigger}
              popperModifiers={{
                preventOverflow: {
                  boundary: document.getElementById("intervention-availability-box")
                }
              }}
              content={
                <StatsPopupDiv className="stats-popup">
                  <div className="title">{getMessage("interventionAvailability.slotStats.title")}</div>
                  <div className="content">
                    <div className="percentage-booked">{percentBookedString}</div>
                    <div className="stats-text">
                      {
                        applyStyles(
                            getMessage("interventionAvailability.slotStats.statsText")
                                .replace("{{numBooked}}", `${rowData.numberNotFreeSlots}`)
                                .replace("{{numTotal}}", `${totalSlots}`)
                        )
                      }
                    </div>
                  </div>
                </StatsPopupDiv>
              }
              flowing
              on="click"
              size="large"
              position="right center"
          />
        </StatsCellDiv>
    )
  }

  dateCellRenderer = ({rowData, cellData}: any) => {
    if (rowData.recurrent) {
      const dateRangeText = moment(rowData.fromDate, "YYYY-MM-DD").format("ddd, DD.MM.YYYY")
          + " - " + moment(rowData.toDate, "YYYY-MM-DD").format("ddd, DD.MM.YYYY");
      return <>
        <span title={dateRangeText}>
          {dateRangeText}
          <Icon name="redo" color="blue" size="small"/>
        </span>
      </>;
    }

    const dateText = moment(cellData, "YYYY-MM-DD").format("ddd, DD.MM.YYYY");
    return <>
      <span title={dateText}>{dateText}</span>
      {
          rowData.recurrentAvailabilityId &&
          <Icon name="redo" color="teal" size="small"/>
      }
    </>;
  }

  timeCellRenderer = ({rowData, cellData}: any) => {
    return `${isoToDisplayTime(rowData.fromTime)} - ${isoToDisplayTime(rowData.toTime)}`;
  }

  statusCellRenderer = ({cellData, rowData}: any) => {
    const {getMessage} = this.props;
    const status = cellData ? getMessage("active") : getMessage("inactive");
    const minAge = rowData.minimumAge

    if (minAge !== null && minAge >= 0) {

      return `${status}  (${minAge}+)`;
    }

    return status;
  }

  selfServiceCellRenderer = ({rowData, cellData}: any) => {
    const {getMessage} = this.props;
    const selfService = rowData.selfService;
    return selfService ? getMessage("button.yes") : getMessage("button.no");
  }

  placeCellRenderer = ({rowData, cellData}: any) => {
    const resourceId: number = rowData.resourceId;
    const resource: ResourceDto = this.state.resources.filter(res => res.id === resourceId)[0];
    return `${resource ? resource.description : ''}`;
  }

  interventionCellRenderer = ({rowData, cellData}: any) => {
    const interventionIds: number[] = rowData.interventionIds;
    let interventionsArr: string[] = [];
    if (interventionIds && interventionIds.length > 0) {
      interventionIds.forEach(id => {
        const intervention = this.state.interventionsMap.get(id);
        if (intervention) {
          interventionsArr.push(intervention.description);
        }
      });
    }
    return interventionsArr.join(", ");
  }

  medicamentCellRenderer = ({rowData, cellData}: any) => {
    const medicamantId: number = rowData.medicamentId;
    if (medicamantId) {
      const medicament: MedicamentDto = this.state.medicaments.filter(med => med.id === medicamantId)[0];
      return `${medicament ? medicament.description : ''} (${rowData.lotNumber})`;
    }

    return "";
  }

  actionsCellRenderer = ({rowData, cellData}: any) => {
    const {foundSlots} = this.state;
    const {getMessage} = this.props;

    const foundSlot = foundSlots.find(slot => slot.id === cellData);

    let type = InterventionAvailabilityType.VACCINATION;
    if (foundSlot && foundSlot.medicamentId === null && foundSlot.lotNumber === null && foundSlot.minimumAge === null) {
      type = InterventionAvailabilityType.OTHER;
    }

    return (
        <React.Fragment>
          {/* TODO ftrajkov 2021-05-13: Refactor component below to not use `EditActionButton` */}
          {/*<EditActionButton
              disabled={foundSlot && foundSlot.active}
              hoverMessage={getMessage('msgActivate')}
              trigger={(<Icon name={'check'}/>)}
              onConfirm={() => this.activateSlot(cellData)}
          />*/}
          {
            <EditActionButton
                hoverMessage={getMessage('msgEdit')}
                onConfirm={() => this.openAddEditModal(type, cellData)}
            />
          }
          <AddActionButton
              hoverMessage={getMessage("interventionAvailability.action.availableSlots")}
              onConfirm={() => this.openAvailableSlotsModal(cellData)}
              trigger={<Icon name={'calendar alternate'}/>}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("interventionAvailability.confirmDelete.message")}
              onConfirm={() => this.deleteSlot(cellData)}
          />
        </React.Fragment>
    );
  };

  renderInterventionSlotModal = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {availableSlotsModalOpen, foundSlots} = this.state;

    const interventionAvailabilityId = this.state.interventionAvailabilityId;
    const availability = foundSlots.find(s => s.id === interventionAvailabilityId);

    return (
        <Modal
            open={availableSlotsModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("interventionAvailability.availableSlots.form.title")}</Modal.Header>
          <FormModalContentContainer>
            <InterventionSlotView
                interventionAvailabilityId={interventionAvailabilityId}
                onCancel={this.hideModal}
                availability={availability}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };

  renderUpsertInterventionAvailabilityModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, interventionAvailabilityType} = this.state;

    const interventionAvailabilityId = this.state.interventionAvailabilityId;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("interventionAvailability.form.title")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertInterventionAvailabilitySlotForm
                interventionAvailabilityId={interventionAvailabilityId}
                interventionAvailabilityType={interventionAvailabilityType}
                onSave={() => {
                  this.hideModal();
                  this.onSearchSlotsSubmitClicked();
                }}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };

}

export default withMessages(withAuthContext(withRouterWorkaround(InterventionAvailabilityView)));
